/**
 * Sidebar (component-inventory.md §2) — the primary navigation. Top to bottom: the firm
 * switcher (PO: firm lives in the sidebar, not the top bar), the app-wide "New case"
 * button (US-010, gated on `cases.create`), then the permission-gated sections (ENG-10
 * catalogue, CC-2). Three renderings off one markup:
 *   >= 900px expanded  — icon + label, grouped under section headings;
 *   >= 900px collapsed — a 60px icon rail (labels move to `title`/`aria-label`), the
 *                        preference persisted by `useNav` (localStorage, §5) and toggled
 *                        from the TopNav;
 *   <  900px           — an off-canvas drawer opened from the TopNav menu button, closed by
 *                        the close button, the backdrop, Escape, or following a link.
 */
import { Link, useRouterState } from '@tanstack/react-router';
// Navigation items take the duotone family (wayfinding); the close button and "New case"
// stay filled — see the rule at the top of ui/icons.ts.
import { Duotone, Plus, X, type IconComponent } from '../ui/icons.ts';
import { useEffect, type JSX } from 'react';

import { usePermission } from '../../lib/permissions/index.tsx';
import { useNav } from '../../store/nav-store.ts';
import { Button, LinkButton } from '../ui/button.tsx';
import { FirmSwitcher } from './firm-switcher.tsx';

interface NavItem {
  label: string;
  to: string;
  icon: IconComponent;
  show: boolean;
  exact?: boolean;
}

interface NavSection {
  heading: string;
  items: NavItem[];
}

function SidebarLink({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate: () => void;
}): JSX.Element {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      className="argus-navlink"
      activeProps={{ className: 'argus-navlink is-active', 'aria-current': 'page' }}
      activeOptions={{ exact: item.exact ?? false }}
      title={collapsed ? item.label : undefined}
      aria-label={collapsed ? item.label : undefined}
      onClick={onNavigate}
    >
      <Icon size={18} aria-hidden="true" />
      <span className="argus-navlink__label">{item.label}</span>
    </Link>
  );
}

export function Sidebar(): JSX.Element {
  const collapsed = useNav((s) => s.sidebarCollapsed);
  const mobileOpen = useNav((s) => s.mobileOpen);
  const setMobileOpen = useNav((s) => s.setMobileOpen);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const canListCases = usePermission('cases.list');
  const canCreateCase = usePermission('cases.create');
  const canReadFirm = usePermission('firm.read');
  const canReadUsers = usePermission('users.read');
  const canReadDsar = usePermission('dsar.read');

  // Close the drawer whenever the route changes (covers programmatic navigation too).
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname, setMobileOpen]);

  // Escape closes the drawer.
  useEffect(() => {
    if (!mobileOpen) return;
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') setMobileOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen, setMobileOpen]);

  const sections: NavSection[] = [
    {
      heading: 'Workspace',
      items: [
        {
          label: 'Dashboard',
          to: '/dashboard',
          icon: Duotone.LayoutDashboard,
          show: true,
          exact: true,
        },
        { label: 'Cases', to: '/cases', icon: Duotone.Briefcase, show: canListCases },
        // PO finding 2026-10-05 — the Client entity; gated like Cases (cases.list).
        { label: 'Clients', to: '/clients', icon: Duotone.UsersRound, show: canListCases },
        // Regulatory reference (T-6.05) — every authenticated user (public material).
        { label: 'Reference', to: '/reference', icon: Duotone.BookOpen, show: true },
      ],
    },
    {
      heading: 'Administration',
      items: [
        { label: 'Team', to: '/settings/team', icon: Duotone.Users, show: canReadUsers },
        // DSAR fulfilment (T-7.14) — managing_partner only (dsar.read).
        {
          label: 'Data requests',
          to: '/settings/dsar',
          icon: Duotone.FileSearch,
          show: canReadDsar,
        },
        {
          label: 'Firm settings',
          to: '/settings/firm',
          icon: Duotone.Building2,
          show: canReadFirm,
        },
      ],
    },
    {
      heading: 'Account',
      items: [
        // Self-service (owed #15 re-enrol authenticator) — every role.
        { label: 'Security', to: '/settings/security', icon: Duotone.ShieldCheck, show: true },
      ],
    },
  ].map((s) => ({ ...s, items: s.items.filter((i) => i.show) }));

  const close = (): void => setMobileOpen(false);

  return (
    <>
      {mobileOpen ? (
        <div className="argus-drawer-backdrop" onClick={close} data-testid="drawer-backdrop" />
      ) : null}
      <nav
        id="primary-nav"
        aria-label="Primary"
        className={[
          'argus-sidebar',
          collapsed ? 'argus-sidebar--collapsed' : '',
          mobileOpen ? 'argus-sidebar--open' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          className="argus-sidebar__close"
          aria-label="Close menu"
          onClick={close}
          icon={<X size={18} aria-hidden="true" />}
        />
        <div className="argus-sidebar__top">
          <FirmSwitcher collapsed={collapsed} />
          {canCreateCase ? (
            <LinkButton
              to="/cases/new"
              variant="primary"
              className="argus-sidebar__new-case"
              icon={<Plus size={16} aria-hidden="true" />}
              aria-label="New case"
              title={collapsed ? 'New case' : undefined}
              onClick={close}
            >
              <span className="argus-navlink__label">New case</span>
            </LinkButton>
          ) : null}
        </div>
        {sections
          .filter((s) => s.items.length > 0)
          .map((section) => (
            <div key={section.heading} role="group" aria-label={section.heading}>
              <div className="argus-sidebar__section" aria-hidden="true">
                {section.heading}
              </div>
              {section.items.map((item) => (
                <SidebarLink key={item.to} item={item} collapsed={collapsed} onNavigate={close} />
              ))}
            </div>
          ))}
        <div className="argus-sidebar__footer">ARGUS · Financial forensics</div>
      </nav>
    </>
  );
}
